import React, { useState } from 'react';
import { Box, Button, Typography, Alert, LinearProgress } from '@mui/material';
import FileUploadZone from '../FileUpload/FileUploadZone';
import { supabaseStorage } from '../../services/supabaseStorage';

/**
 * Test component for Supabase Storage integration
 */
const SupabaseTestComponent = () => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState('');

  const handleFileUpload = async (files) => {
    if (!files.length) return;

    try {
      setError('');
      setUploadResult(null);
      
      const file = files[0];
      
      const result = await supabaseStorage.uploadFile(file, {
        folder: 'test-uploads',
        isPublic: true,
        onProgress: setUploadProgress
      });

      if (result.success) {
        setUploadResult(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const testBucketList = async () => {
    try {
      const result = await supabaseStorage.listFiles('test-uploads');
      console.log('Bucket contents:', result);
      
      if (result.success) {
        alert(`Found ${result.files.length} files in test-uploads folder`);
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Supabase Storage Test
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload a test file to verify Supabase Storage is working correctly.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {uploadResult && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Upload successful!</strong><br />
            File: {uploadResult.fileName}<br />
            Size: {(uploadResult.size / 1024).toFixed(1)} KB<br />
            {uploadResult.publicUrl && (
              <>
                URL: <a href={uploadResult.publicUrl} target="_blank" rel="noopener noreferrer">
                  View File
                </a>
              </>
            )}
          </Typography>
        </Alert>
      )}

      <FileUploadZone
        onFilesSelected={handleFileUpload}
        acceptedFileTypes={{
          'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
          'application/pdf': ['.pdf']
        }}
        maxFiles={1}
        maxSize={5 * 1024 * 1024} // 5MB
      />

      {uploadProgress > 0 && uploadProgress < 100 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            Uploading: {uploadProgress.toFixed(1)}%
          </Typography>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      )}

      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
        <Button variant="outlined" onClick={testBucketList}>
          List Test Files
        </Button>
        <Button 
          variant="outlined" 
          onClick={() => window.open('https://supabase.com/dashboard/project/' + process.env.REACT_APP_SUPABASE_URL?.split('//')[1]?.split('.')[0] + '/storage/buckets', '_blank')}
        >
          Open Supabase Dashboard
        </Button>
      </Box>
    </Box>
  );
};

export default SupabaseTestComponent;